import type { SxProps, Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import type React from 'react';

// ─── Typography label style helper ───────────────────────────────────────────
export const getLabelSx = (overrides: SxProps<Theme> = {}) => ({
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  ...overrides
});

// ─── Typography description style helper ─────────────────────────────────────
export const getDescriptionSx = (overrides: SxProps<Theme> = {}) => ({
  fontFamily: 'AC Nord Text',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
  ...overrides
});

// ─── Focus-visible outline (a11y) ─────────────────────────────────────────────
const focusVisibleOutline = {
  outline: '2px solid black',
  outlineOffset: '2px'
} as const;

// ─── Card container ───────────────────────────────────────────────────────────
export const cardSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: { xs: '100%', md: '804px' },
  minHeight: { xs: 'fit-content', md: '358px' },
  borderRadius: '16px',
  p: { xs: '0px', sm: '24px', md: '24px' },
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 4, md: 6 },
  background: { xs: 'transparent', sm: '#FFFFFF' },
  boxShadow: { xs: '', md: '0px 0px 15px 0px #00000026' },
  boxSizing: 'border-box',
  opacity: 1,
  mt: { xs: 2, md: 3 },
  '& :focus-visible, & :focus': focusVisibleOutline
};

// ─── Section title ─────────────────────────────────────────────────────────────
export const sectionTitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 700,
  fontSize: { xs: '18px', md: '20px' },
  lineHeight: '28px',
  letterSpacing: '0px',
  color: '#000000',
  mt: { md: '0px', xs: '16px' }
};

// ─── Icon + input row ─────────────────────────────────────────────────────────
export const iconInputSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  height: 'fit-content'
};

// ─── Form footer ──────────────────────────────────────────────────────────────
export const footerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row' },
  justifyContent: { xs: 'space-between' },
  gap: { xs: 2, sm: 0 },
  pt: '8px'
};

// ─── TextField input text ─────────────────────────────────────────────────────
export const textFieldInputSx: SystemStyleObject<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: { xs: '14px', md: '16px' },
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

// ─── TextField underline overrides ────────────────────────────────────────────
export const textFieldUnderlineSx: SystemStyleObject<Theme> = {
  '& .MuiInput-underline:before': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  },
  '& .MuiInput-underline.Mui-focused:after': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  }
};

// ─── TextField floating label ─────────────────────────────────────────────────
export const textFieldLabelSx: SystemStyleObject<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: { xs: '14px', md: '16px' },
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  '&.Mui-focused': {
    color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
    fontWeight: 500,
    fontSize: { xs: '14px', md: '16px' }
  },
  '&.MuiInputLabel-shrink': {
    color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)',
    fontWeight: 500,
    fontSize: { xs: '14px', md: '16px' }
  },
  "& .MuiFormLabel-asterisk": {
    color: "#FF333A", 
  }
};

// ─── Select placeholder / selected value text ─────────────────────────────────
export const renderValuePlaceholderStyle: React.CSSProperties = {
  color: '#888',
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px'
};

export const renderValueSelectedStyle: React.CSSProperties = {
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0
};

// ─── Screen reader alert (visually hidden live region) ─────────────────────────
export const screenReaderAlertSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0,0,0,0)',
  margin: '-1px',
  border: 0,
  padding: 0,
  whiteSpace: 'nowrap'
};

// ─── Review row label / value (Step 4) ────────────────────────────────────────
export const reviewRowLabelSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)'
};

export const reviewRowValueSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

// ─── Field error text ─────────────────────────────────────────────────────────
export const fieldErrorTextSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 400,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-error, #851109)'
};

// ─── Calendar nav button ──────────────────────────────────────────────────────
export const getCalendarNavButtonSx = (
  disabled: boolean,
  position: 'left' | 'right'
): SxProps<Theme> => ({
  position: 'absolute',
  [position]: {xs: -30, sm: -40, md:-55},
  transform: 'translateY(400%)',
  pointerEvents: 'all',
  width: { xs: '32px', sm: '40px', md: '48px' },
  height: { xs: '32px', sm: '40px', md: '48px' },
  borderRadius: '100px',
  boxShadow: '0px 0px 15px 0px #00000026',
  background: disabled
    ? 'var(--Sem-object-fills-colour-bg-fill-secondary-disabled, #EFF0F2)'
    : 'var(--Sem-object-fills-colour-bg-fill-secondary, #FFFFFF)',
  border: 'none',
  cursor: disabled ? 'default' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

// ─── Calendar month title ─────────────────────────────────────────────────────
export const calendarMonthTitleSx: SxProps<Theme> = {
  fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
  fontWeight: 700,
  fontSize: '18px',
  lineHeight: '24px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  textAlign: 'center'
};

// ─── Calendar close button ────────────────────────────────────────────────────
export const calendarCloseButtonSx: SxProps<Theme> = {
  position: 'absolute',
  right: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '24px',
  height: '24px'
};

// ─── Date picker clear button ─────────────────────────────────────────────────
export const datePickerClearButtonSx: SxProps<Theme> = {
  textDecoration: 'underline',
  fontFamily: 'AC Nord Text',
  fontWeight: 700,
  fontStyle: 'bold',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  textAlign: 'center',
  verticalAlign: 'middle',
  textDecorationStyle: 'solid' as const,
  textDecorationOffset: 0,
  textDecorationThickness: 0
};
// ─── Date picker footer (Clear / Select buttons row) ───────────────────────────────
// hasValue controls alignment: space-between when Clear is visible, flex-end otherwise.
export const getDatePickerFooterSx = (hasValue: boolean): SxProps<Theme> => ({
  display: 'flex',
  flexDirection: { xs: 'column-reverse', sm: 'row', md: 'row' },
  justifyContent: hasValue ? 'space-between' : 'flex-end',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: 1,
  mt: '16px'
});
// ─── Date picker calendar popup container ─────────────────────────────────────
export const datePickerPopupSx: SxProps<Theme> = {
  position: 'absolute',
  outline: 'none',
  top: '100%',
  width: { xs: 'max-content', md: 'max-content' },
  minHeight: { xs: 'auto', md: '473px' },
  left: '50%',
  right: 'auto',
  transform: 'translateX(-50%)',
  zIndex: 1,
  borderRadius: '16px',
  padding: { xs: '16px', md: '32px' },
  background: 'var(--Sem-object-fills-colour-bg-fill-card-primary, #FFFFFF)',
  boxShadow: '0px 0px 15px 0px #00000026',
  mt: '4px',
  overflow: 'visible',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  '& .rdp': {
    '--rdp-accent-color': '#1460AA',
    '--rdp-background-color': 'rgba(20, 96, 170, 0.1)',
    '--rdp-cell-size': '45px',
    // Shrink calendar cells on narrow screens
    '@media (max-width: 600px)': {
      '--rdp-cell-size': '36px'
    },
    margin: 0,
    flex: 1,
    position: 'relative'
  },
  '& .rdp-caption': { display: 'block', width: '100%' },
  '& .rdp-head_cell': {
    fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
    fontWeight: 400,
    fontSize: { xs: '11px', md: '12px' },
    lineHeight: '16px',
    letterSpacing: '0px',
    color: 'var(--Sem-text-colour-text-primary, #000000)',
    textTransform: 'none',
    textAlign: 'center',
    paddingBottom: '8px'
  },
  '& .rdp-cell': { padding: { xs: '2px', md: '4px' } },
  '& .rdp-day': {
    width: { xs: '30px', md: '35px' },
    height: { xs: '30px', md: '35px' },
    fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: { xs: '14px', md: '18px' },
    lineHeight: '20px',
    letterSpacing: '0px',
    color: 'var(--Sem-text-colour-text-primary, #000000)'
  },
  '& .rdp-day_selected': {
    borderRadius: '100px !important',
    padding: '10px',
    background: 'var(--Sem-icon-colour-icon-primary, #1460AA) !important',
    color: '#FFFFFF !important',
    opacity: 1
  }
};

// ─── Radio question text ──────────────────────────────────────────────────────
export const radioQuestionTextSx: SxProps<Theme> = {
  fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
  fontWeight: 500,
  fontSize: { xs: '14px', md: '16px' },
  lineHeight: '20px',
  letterSpacing: '0px',
  color: '#000000',
  m: 0
};

export const radioSx: SxProps<Theme> = {
  color: '#1460AA',
  '&.Mui-checked': { color: '#1460AA' },
  '&.Mui-focusVisible, &:focus-within': {
    outline: '2px solid black',
    outlineOffset: '-3px'
  },
    p: 0,
    width: 24,
    height: 24,
    angle: '0deg',
    opacity: 1,

};

export const radioFormControlLabelSx: SxProps<Theme> = {
  m: 0,
  gap: '8px',
  '& .MuiFormControlLabel-label': {
    fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0px',
    color: '#000000'
  }
};

// ─── Custom select / dropdown ─────────────────────────────────────────────────
export const getSelectFloatingLabelSx = (isActive: boolean): SxProps<Theme> => ({
  position: 'absolute',
  left: 0,
  top: -16,
  transformOrigin: 'top left',
  transition: 'transform 200ms cubic-bezier(0.0, 0, 0.2, 1), color 200ms',
  transform: isActive
    ? 'translate(0, -1.5px) scale(0.75)'
    : 'translate(0, 20px) scale(1)',
  pointerEvents: 'none',
  fontFamily: "'AC Nord Text', 'AC Nord', sans-serif",
  fontWeight: 500,
  fontSize: { xs: '14px', md: '16px' },
  lineHeight: '20px',
  letterSpacing: '0px',
  color: isActive
    ? 'var(--Sem-text-colour-text-secondary, #6D6D6F)'
    : 'var(--Sem-text-colour-text-primary, #000000)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: isActive ? '133%' : '100%'
});

export const getSelectTriggerSx = (error: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  p: '4px 0',
  minHeight: '32px',
  borderBottom: `1px solid ${
    error
      ? 'var(--Sem-text-colour-text-error, #851109)'
      : 'var(--Sem-brdr-colour-border-secondary, #C9CACC)'
  }`,
  outline: 'none',
  '&:focus-visible, &:focus-within': { borderBottomWidth: '2px', ...focusVisibleOutline }
});

export const selectDropdownSx: SxProps<Theme> = {
  position: 'absolute',
  top: '100%',
  left: 0,
  width: '100%',
  zIndex: 1,
  maxHeight: '225px',
  overflowY: 'auto',
  overflowX: 'hidden',
  py: '4px',
  borderBottomLeftRadius: '16px',
  borderBottomRightRadius: '16px',
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  boxShadow: '0px 0px 15px 0px #00000026',
  background: '#FFFFFF',
  mt: '4px'
};

export const getSelectOptionSx = (
  isSelected: boolean,
  isActive: boolean
): SxProps<Theme> => ({
  height: '56px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px',
  boxSizing: 'border-box',
  fontFamily: 'AC Nord Text',
  fontWeight: isSelected ? 500 : 400,
  fontStyle: 'normal',
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)',
  backgroundColor: isSelected ? '#EFF0F2' : isActive ? '#F9F9F9' : '#FFFFFF',
  outline: isActive ? '2px solid black' : 'none',
  outlineOffset: '-10px',
  cursor: 'pointer',
  userSelect: 'none' as const,
  WebkitTapHighlightColor: 'transparent',
  '&:hover': { backgroundColor: '#F9F9F9'},
  '&:active': { backgroundColor: '#EFF0F2' , outline: '1px solid black', outlineOffset: '2px' }
});

// ─── POS edit link button ─────────────────────────────────────────────────────
export const posEditLinkButtonSx: SxProps<Theme> = {
  p: 0,
  fontFamily: 'AC Nord Text',
  fontWeight: 700,
  fontSize: '14px',
  lineHeight: '20px',
  textDecoration: 'underline',
  color: 'var(--Sem-text-colour-text-link, #1460AA)',
  minWidth: 0
};

// ─── POS overflow badge ───────────────────────────────────────────────────────
export const posOverflowBadgeSx: SxProps<Theme> = {
  width: 47,
  height: 40,
  gap: 8,
  opacity: 1,
  paddingTop: 'var(--spacing-spacing-100, 4px)',
  paddingRight: 'var(--spacing-spacing-200, 8px)',
  paddingBottom: 'var(--spacing-spacing-100, 4px)',
  paddingLeft: 'var(--spacing-spacing-200, 8px)',
  borderRadius: 'var(--radii-radius-max, 999px)',
  borderWidth: '1px',
  borderStyle: 'solid',
  background: 'var(--Sem-object-fills-colour-bg-fill-secondary-pressed, #EFF0F2)',
  borderColor: 'var(--Sem-brdr-colour-border-primary-pressed, #002345)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export const posOverflowBadgeTextSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'Medium',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  textAlign: 'center',
  verticalAlign: 'middle',
  color: 'var(--Sem-text-colour-text-label-pressed, #002345)'
};

// ─── POS country name / IATA value ────────────────────────────────────────────
export const posCountryNameSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-secondary, #6D6D6F)'
};

export const posCountryIataSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text, sans-serif',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  color: 'var(--Sem-text-colour-text-primary, #000000)'
};

// ─── Step 1 POS question label ────────────────────────────────────────────────
export const getStep1PosLabelSx = (hasError: boolean, hasBizModel: boolean): SxProps<Theme> => ({
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: { xs: '14px', sm: '16px' },
  lineHeight: '20px',
  letterSpacing: '0px',
  color: hasBizModel ? 'var(--Sem-text-colour-text-primary, #000000)' : 'var(--Sem-text-colour-text-disabled, #B7C9D9)',
  verticalAlign: 'middle',
  m: 0,
  borderBottom: '1px solid',
  borderColor: hasError
    ? 'var(--Sem-text-colour-text-error, #851109)'
    : 'var(--Sem-brdr-colour-border-secondary, #C9CACC)',
  paddingBottom: '6px',
  width: { xs: '100%' }
});

// ─── Sales mix section h4 title ───────────────────────────────────────────────
export const salesMixTitleSx: SxProps<Theme> = {
  fontFamily: 'AC Nord Text',
  fontWeight: 500,
  fontStyle: 'normal',
  fontSize: '16px',
  lineHeight: '20px',
  letterSpacing: '0px',
  verticalAlign: 'middle',
  mb: '16px',
  mt: 0
};

// ─── Step 4 POS section review label ─────────────────────────────────────────
export const step4PosLabelSx: SxProps<Theme> = {
  fontSize: '12px',
  color: '#888',
  fontWeight: 400
};

// ─── TextField error underline overrides ──────────────────────────────────────
export const textFieldErrorUnderlineSx: SystemStyleObject<Theme> = {
  '& .MuiInput-underline:before': {
    borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)'
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)'
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'var(--Sem-text-colour-text-error, #851109)'
  }
};

// ─── TextField helper text ────────────────────────────────────────────────────
export const helperTextSx: SystemStyleObject<Theme> = {
  '& .MuiFormHelperText-root': {
    fontFamily: 'AC Nord Text',
    fontSize: '11px',
    fontWeight: 400,
    color: '#888',
    mt: '4px',
    ml: 0
  },
  "& .MuiFormLabel-asterisk": {
    color: "#FF333A", 
  }
};

// ─── Review icon row image ────────────────────────────────────────────────────
export const reviewIconImageSx: SxProps<Theme> = {
  width: '24px',
  height: '24px',
  objectFit: 'contain',
  color: '#888'
};

// ─── Calendar caption container ───────────────────────────────────────────────
export const calendarCaptionContainerSx: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  mb: '8px'
};

// ─── Calendar nav arrow images ────────────────────────────────────────────────
export const calendarNavArrowPrevStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  transform: 'rotate(180deg)',
  objectFit: 'contain',
  
};

export const calendarNavArrowNextStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  objectFit: 'contain',
  color:'#1460AA'
};

// ─── Date picker — calendar trigger icon ─────────────────────────────────────
export const datePickerCalendarIconSx: SxProps<Theme> = {
  width: 20,
  height: 20,
  objectFit: 'contain',
  mb: '8px'
};

// ─── Date picker — dropdown arrow indicator ───────────────────────────────────
export const getDatePickerArrowIndicatorSx = (open: boolean): SxProps<Theme> => ({
  display: 'flex',
  alignItems: 'center',
  mb: '8px',
  transition: 'transform 0.2s',
  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
  pointerEvents: 'none'
});

// ─── Date picker — arrow image inside indicator ───────────────────────────────
export const datePickerArrowImgSx: SxProps<Theme> = {
  width: 10,
  height: 5,
  objectFit: 'contain',
  mr: '8px'
};

// ─── Date picker — helper text below field ────────────────────────────────────
export const datePickerHelperTextSx: SxProps<Theme> = {
  fontSize: '12px',
  color: '#888',
  mt: '4px'
};

// ─── Custom select — CSS triangle arrow ──────────────────────────────────────
export const getSelectArrowSx = (open: boolean): SxProps<Theme> => ({
  width: 0,
  height: 0,
  borderLeft: '5px solid transparent',
  borderRight: '5px solid transparent',
  borderTop: open ? 'none' : '5px solid #000000',
  borderBottom: open ? '5px solid #000000' : 'none',
  flexShrink: 0,
  ml: '8px'
});

// ─── Dropdown option checkmark image ─────────────────────────────────────────
export const dropdownCheckmarkSx: SxProps<Theme> = {
  width: '20px',
  height: '20px',
  flexShrink: 0,
  objectFit: 'contain',
  color: '#002345'
};

// ─── POS — "Select POS" link button ──────────────────────────────────────────
export const posSelectLinkButtonSx: SxProps<Theme> = {
  textDecoration: 'underline',
  fontSize: { xs: '14px', sm: '16px' }
};

// ─── POS — plus icon ─────────────────────────────────────────────────────────
export const posPlusIconSx: SxProps<Theme> = {
  opacity: 1,
  padding: '0px',
  objectFit: 'contain'
};

// ─── Form field start-adornment icon (address / website / phone / email) ──────
export const fieldIconSx: SxProps<Theme> = {
  width: '24px',
  height: '24px',
  objectFit: 'contain',
  marginBottom: '4px'
};

// ─── Admin contact — same-as-business checkbox ────────────────────────────────
export const adminCheckboxSx: SxProps<Theme> = {
  p: '4px',
  color: 'var(--Sem-icon-colour-icon-primary, #1460AA)',
  '&.Mui-checked': {
    color: 'var(--Sem-icon-colour-icon-primary, #1460AA)'
  },
  '&.Mui-focusVisible': {
    outline: '1px solid black',
    outlineOffset: '2px'
  }
};

// ─── Step 4 — empty fallback text ─────────────────────────────────────────────
export const fallbackTextSx: SxProps<Theme> = {
  fontSize: '14px',
  color: '#888'
};

// ─── Step 4 — POS items stack ─────────────────────────────────────────────────
export const step4PosStackSx: SxProps<Theme> = {
  gap: 3,
  flexWrap: 'wrap',
  px: { xs: 1, md: 2 }
};

// ─── ReviewRow container ───────────────────────────────────────────────────────
export const reviewRowContainerSx: SxProps<Theme> = {
  gap: '2px'
};

// ─── SalesMixReviewItem container ─────────────────────────────────────────────
export const salesMixReviewItemContainerSx: SxProps<Theme> = {
  gap: '1px'
};

// ─── CommonFooter — individual button responsive flex ─────────────────────────
export const footerButtonSx: SxProps<Theme> = {
  flex: { xs: 1, sm: "initial" }
};

// ─── FieldError — error icon image ───────────────────────────────────────────
export const fieldErrorIconSx: SxProps<Theme> = {
  width: '16px',
  height: '16px',
  objectFit: 'contain'
};

// ─── FieldError — stack container ────────────────────────────────────────────
export const fieldErrorStackSx: SxProps<Theme> = {
  mt: '4px'
};

// ─── Calendar close icon ──────────────────────────────────────────────────────
export const calendarCloseIconSx: SxProps<Theme> = {
  width: 16,
  height: 16,
  objectFit: 'contain',
  display: 'block'
};

// ─── SvgIcon ──────────────────────────────────────────────────────────────────

export const getSvgIconSx = (
  src: string,
  color: string,
  w: number,
  h: number,
  overrides: SxProps<Theme> = {}
): SxProps<Theme> => ({
  display: 'inline-block',
  flexShrink: 0,
  width: w,
  height: h,
  backgroundColor: color,
  maskImage: `url(${src})`,
  WebkitMaskImage: `url(${src})`,
  maskSize: 'contain',
  WebkitMaskSize: 'contain',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskPosition: 'center',
  ...overrides
});

